import { Badge } from '@mui/material';
import React, { ReactNode, useRef } from 'react';
import { AvatarProps } from './Avatar.types';
import {
  StyledAvatarContainer,
  StyledAvatarText,
  StyledBadgeContent,
} from './Avatar.styles';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { TextProps } from '@components/Text/Text';

const Avatar: React.FC<AvatarProps> = ({
  size = 'large',
  user,
  editable = false,
  onIconClick,
  onDeleteIconClick,
}) => {
  const buttonInputRef = useRef<HTMLInputElement>(null);

  const renderButtonIcon = (): ReactNode => {
    if (!user.avatar) {
      return <AddCircleIcon color='primary' fontSize='inherit' />;
    } else {
      return <DeleteIcon color='error' fontSize='inherit' />;
    }
  };

  const getAvatarTextVariant = (): TextProps => {
    if (size === 'small') {
      return { variant: 'h6', fontWeight: 'fontWeightRegular' };
    } else if (size === 'medium') {
      return { variant: 'h5', fontWeight: 'fontWeightMedium' };
    }
    return { variant: 'h3', fontWeight: 'fontWeightBold' };
  };

  const handleIconClick = () => {
    if (user.avatar && onDeleteIconClick) {
      onDeleteIconClick();
      return;
    }

    if (buttonInputRef?.current?.click) buttonInputRef.current.click();
  };

  const handleFileChange = (event: any) => {
    const fileObj = event.target.files && event.target.files[0];
    if (!fileObj) {
      return;
    }

    const formData = new FormData();
    formData.append('file', fileObj);

    if (onIconClick) onIconClick(formData);
  };

  return (
    <Badge
      overlap='circular'
      invisible={!editable}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      badgeContent={
        <StyledBadgeContent
          data-testid={`${
            user.avatar ? 'delete' : 'add'
          }-avatar-button-icon`}
          size={size}
          iconButtonType={user.avatar ? 'delete' : 'add'}
          onClick={handleIconClick}
        >
          <input
            data-testid='avatar-upload-input'
            style={{ display: 'none' }}
            ref={buttonInputRef}
            type='file'
            onChange={handleFileChange}
            accept='image/png, image/jpeg, image/gif'
          />
          {renderButtonIcon()}
        </StyledBadgeContent>
      }
    >
      <StyledAvatarContainer size={size} src={user.avatar}>
        {user?.firstName && user?.lastName ? (
          <StyledAvatarText {...getAvatarTextVariant()}>
            {`${user?.firstName?.[0]}${user?.lastName?.[0]}`.toUpperCase()}
          </StyledAvatarText>
        ) : null}
      </StyledAvatarContainer>
    </Badge>
  );
};

export default Avatar;
